import {
  Outcome,
  Representation,
} from "@financial-advisory/data-access/api-types";
import { ProductTree } from "@financial-advisory/data-access/api-types";
import { LogicalOperator } from "@financial-advisory/data-access/api-types";
import {
  ChangedNode,
  QuestionAnswer,
  SystemState,
  TreeValue,
} from "../lib/types";

export const isOutcomeDecisive = (outcome: Outcome): boolean => {
  return outcome === Outcome.TRUE || outcome === Outcome.FALSE;
};

export const getDedupedLeafNodes = (allLeafNodes: Representation[]) => {
  return allLeafNodes.reduce(
    (acc: Map<string, QuestionAnswer>, node: Representation) => {
      const existing = acc.get(node.question);
      const outcome = node.value ?? Outcome.UNDECIDED;
      if (!existing || isOutcomeDecisive(outcome)) {
        if (isOutcomeDecisive(outcome) && !node.reasoning) {
          console.debug(
            `Leaf node ${node.question} has a decisive outcome but no reasoning`
          );
        }
        const leafNode: QuestionAnswer = {
          question: node.question,
          value: node.value ?? Outcome.UNDECIDED,
          reasoning: node.reasoning || existing?.reasoning,
        };
        acc.set(node.question, leafNode);
      }
      return acc;
    },
    new Map<string, QuestionAnswer>()
  );
};

export const deduplicateTreesByQuestion = (
  trees: ProductTree[]
): ProductTree[] => {
  const treeMap = trees.reduce(
    (acc: Map<string, ProductTree>, tree: ProductTree) => {
      const key = normaliseString(tree.template.question);
      if (!acc.has(key)) {
        acc.set(key, tree);
      }
      return acc;
    },
    new Map<string, ProductTree>()
  );
  return Array.from(treeMap.values());
};

const flattenTemplate = (template: Representation): Representation[] => {
  if (!template.subConditions) {
    return [template];
  } else {
    return template.subConditions.flatMap((subCondition) =>
      flattenTemplate(subCondition)
    );
  }
};

const findTemplates = (
  template: Representation,
  matchName: string
): Representation[] => {
  if (template.name.toLowerCase().includes(matchName.toLowerCase())) {
    return [template];
  }
  if (!template.subConditions) {
    return [];
  } else {
    return template.subConditions.flatMap((subCondition) =>
      findTemplates(subCondition, matchName)
    );
  }
};

export const getTreesByMatchName = (
  productTrees: ProductTree[],
  matchName: string
): ProductTree[] => {
  return productTrees.flatMap((tree) => {
    return findTemplates(tree.template, matchName).map(
      (template): ProductTree => {
        return {
          template: template,
          description: "",
          preferenceRank: 0,
          id: template.name,
          name: template.question ?? "",
        };
      }
    );
  });
};

/**
 * Tree type classification based on productTreeId naming convention.
 */
export type TreeType = "situation" | "segment" | "suggestion";

/**
 * Determines the tree type (situation, segment, or suggestion) based on the productTreeId.
 * Uses a naming convention where the ID contains "segment" or "situation" to classify.
 *
 * @param productTreeId - The product tree ID to classify
 * @returns The tree type: "situation", "segment", or "suggestion" (default)
 */
export const getTreeTypeFromId = (productTreeId: string): TreeType => {
  const treeIdLower = productTreeId.toLowerCase();
  if (treeIdLower.includes("segment")) {
    return "segment";
  } else if (treeIdLower.includes("situation")) {
    return "situation";
  }
  return "suggestion";
};

export const classifyByTreeType = (treeValues: TreeValue[]): SystemState => {
  return [...new Set(treeValues)].reduce(
    (acc: SystemState, treeValue: TreeValue) => {
      const treeType = getTreeTypeFromId(treeValue.productTreeId);
      if (treeType === "segment") {
        acc.segments.set(treeValue.productTreeId, treeValue);
      } else if (treeType === "situation") {
        acc.situations.set(treeValue.productTreeId, treeValue);
      } else {
        acc.suggestions.set(treeValue.productTreeId, treeValue);
      }
      return acc;
    },
    {
      situations: new Map(),
      segments: new Map(),
      suggestions: new Map(),
    }
  );
};

export const mergeOldAndNewQuestionAnswers = (
  oldQuestionAnswers: QuestionAnswer[],
  newQuestionAnswers: QuestionAnswer[]
): QuestionAnswer[] => {
  return newQuestionAnswers.map((newQuestionAnswer) => {
    const oldQuestionAnswer = oldQuestionAnswers.find(
      (oldQuestionAnswer) =>
        oldQuestionAnswer.question === newQuestionAnswer.question
    );
    if (oldQuestionAnswer?.reasoning && !newQuestionAnswer.reasoning) {
      return {
        ...newQuestionAnswer,
        reasoning: oldQuestionAnswer.reasoning,
      };
    } else {
      return newQuestionAnswer;
    }
  });
};

export const normaliseString = (string: string): string => {
  return string.toLowerCase().trim();
};

export const getSystemStateValues = (
  productTrees: ProductTree[],
  leafNodes: QuestionAnswer[],
  preCalculatedTreeValues: TreeValue[] = []
): SystemState => {
  const treeValues = assignValuesToTrees(
    productTrees,
    leafNodes,
    preCalculatedTreeValues
  );
  return classifyByTreeType(treeValues);
};

const assignValuesToTrees = (
  productTrees: ProductTree[],
  leafNodes: QuestionAnswer[],
  preCalculatedTreeValues: TreeValue[] = []
): TreeValue[] => {
  return productTrees.map((productTree) => {
    const preCalculatedValue =
      preCalculatedTreeValues.find(
        (value) => value.productTreeId === productTree.id
      )?.value ?? Outcome.UNDECIDED;
    const propagatedValue = propagateValue(productTree.template, leafNodes);
    if (
      !isOutcomeDecisive(propagatedValue) &&
      isOutcomeDecisive(preCalculatedValue)
    ) {
      return {
        productTreeId: productTree.id,
        name: productTree.name,
        value: preCalculatedValue,
      };
    } else {
      return {
        productTreeId: productTree.id,
        name: productTree.name,
        value: propagatedValue,
      };
    }
  });
};

/**
 * Normalizes propagateWhen values to Outcome enum values.
 * Handles both string values (e.g., "TRUE") and Outcome enum values.
 */
const normalizePropagateWhen = (
  propagateWhen: Outcome[] | string[] | undefined | null
): Outcome[] | undefined => {
  if (!propagateWhen || !Array.isArray(propagateWhen)) return undefined;
  return propagateWhen.map((v) =>
    typeof v === "string" ? ((Outcome as any)[v] ?? v) : v
  ) as Outcome[];
};

/**
 * Checks if a child node's value should "count" toward its parent's evaluation
 * based on the propagateWhen constraint.
 * If propagateWhen is not set, the child always counts.
 * If propagateWhen is set, the child only counts if its value is in the list.
 */
const doesChildCount = (
  childValue: Outcome,
  propagateWhen: Outcome[] | string[] | undefined | null
): boolean => {
  const normalizedPropagateWhen = normalizePropagateWhen(propagateWhen);
  if (!normalizedPropagateWhen || normalizedPropagateWhen.length === 0) {
    return true; // No constraint, child always counts
  }
  return normalizedPropagateWhen.includes(childValue);
};

export const propagateValue = (
  template: Representation,
  leafNodes: QuestionAnswer[]
): Outcome => {
  if (!template.subConditions) {
    const matchedValue = leafNodes.find(
      (leafNode) => leafNode.question === template.question
    );
    if (matchedValue) {
      return matchedValue.value;
    } else {
      //console.error(`Leaf node ${template.question} not found in leafNodes`);
      return Outcome.UNDECIDED;
    }
  } else {
    // Collect child values, filtering by propagateWhen
    const countingResults: Outcome[] = [];
    template.subConditions.forEach((subCondition: Representation) => {
      const childValue = propagateValue(subCondition, leafNodes);
      // Only include child values that satisfy their propagateWhen constraint
      if (doesChildCount(childValue, subCondition.propagateWhen)) {
        countingResults.push(childValue);
      }
    });

    // If no children count, the parent is TRUE (all conditions are satisfied or don't apply)
    if (countingResults.length === 0) {
      return Outcome.TRUE;
    }

    if (template.logicalOperator === LogicalOperator.AND) {
      if (countingResults.every((result) => result === Outcome.TRUE)) {
        return Outcome.TRUE;
      } else if (countingResults.some((result) => result === Outcome.FALSE)) {
        return Outcome.FALSE;
      } else {
        return Outcome.UNDECIDED;
      }
    }
    if (template.logicalOperator === LogicalOperator.OR) {
      if (countingResults.some((result) => result === Outcome.TRUE)) {
        return Outcome.TRUE;
      } else if (countingResults.every((result) => result === Outcome.FALSE)) {
        return Outcome.FALSE;
      } else {
        return Outcome.UNDECIDED;
      }
    }
    if (template.logicalOperator === LogicalOperator.NOT) {
      if (countingResults.length !== 1) {
        console.error(
          `NOT node only takes one input, got ${countingResults.length} inputs`
        );
        return Outcome.UNDECIDED;
      }
      if (countingResults.every((result) => result === Outcome.TRUE)) {
        return Outcome.FALSE;
      } else if (countingResults.every((result) => result === Outcome.FALSE)) {
        return Outcome.TRUE;
      } else {
        return Outcome.UNDECIDED;
      }
    } else {
      console.error(
        `Unrecognised logical operator: ${template.logicalOperator}`
      );
      return Outcome.UNDECIDED;
    }
  }
};

export const groupNodes = (
  productTrees: ProductTree[]
): Map<string, ChangedNode> => {
  return (
    productTrees.reduce((acc: Map<string, ChangedNode>, tree: ProductTree) => {
      const leafNodes = flattenTemplate(tree.template);
      leafNodes.forEach((node) => {
        const key = normaliseString(node.question);
        if (!acc.has(key)) {
          acc.set(key, {
            name: node.name,
            question: node.question,
            value: Outcome.UNDECIDED,
            usedIn: new Set<string>([tree.name]),
            usedInIds: new Set<string>([tree.id]),
          });
        } else {
          acc.get(key)?.usedIn.add(tree.name);
          acc.get(key)?.usedInIds.add(tree.id);
        }
      });
      return acc;
    }, new Map<string, ChangedNode>()) ?? new Map<string, ChangedNode>()
  );
};

export const cleanTreeNames = (productTree: ProductTree[]) => {
  return productTree.map((tree) => {
    // Find text inside ''
    const match = tree.name.match(/'([^']+)'/);
    return {
      ...tree,
      name: match ? match[1] : tree.name,
    };
  });
};
